import { useMemo, useState } from 'react';
import { Divider, Space, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    EditOutlined,
    DeleteOutlined,
    HomeOutlined,
    PlusOutlined,
    DownOutlined,
    UpOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import DotSeparator from 'components/new-ui/base/dot-separator/DotSeparator';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import ShipmentIcon from 'components/icons/ShipmentIcon';
import { H2 } from 'components/new-ui/base/typography/styles';
import { formatMass } from 'core/utils/numbers';
import { ICS2ConsignmentHouseLevel, ICS2GoodsItemResponse } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { colors } from 'theme';
import { InvalidIcon } from '../../Form.styles';
import {
    CardItem,
    ContentWrap,
    FooterRow,
    HeaderLeft,
    IconWrap,
    InfoRow,
    MetaText,
    Strong,
    HeaderRow,
    ProductCell,
    ProductRow,
    ActionsCell,
    StyledCollapse,
    ProductsList,
} from '../../styles';

const { Panel } = StyledCollapse;

const EMPTY_VALUE = '-';

const GROSS_MASS_FORMAT = { fractionDigits: 2, unit: '' };

type Props = {
    data: ICS2ConsignmentHouseLevel;
    consignmentNumber: number;
    viewOnly: boolean | undefined;
    items?: ICS2GoodsItemResponse[];
    hasErrors?: boolean;
    invalidItemNumbers?: Set<number>;
    onDelete?: (houseConsignmentId: string) => Promise<void>;
    onAddItem?: (houseConsignmentId: string) => Promise<void>;
    onDeleteItem?: (productId: string) => Promise<void>;
};

const HouseConsignmentListItem = ({
    data,
    consignmentNumber,
    viewOnly,
    items = [],
    hasErrors,
    invalidItemNumbers,
    onDelete,
    onAddItem,
    onDeleteItem,
}: Props) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [seeDetails, setSeeDetails] = useState(false);

    /**
     * A goods item that failed marks the consignment holding it too: the items are only on screen
     * once the card is expanded, so without it the failure has nothing to show for it in the list.
     * A consignment with no goods items at all is reported on its own cards instead, so it counts as
     * a consignment level failure rather than an item one.
     */
    const hasItemErrors = Boolean(invalidItemNumbers?.size);
    const isInvalid = hasErrors || hasItemErrors;

    const activeKey = useMemo(() => (seeDetails ? ['details'] : []), [seeDetails]);

    const navigateToHouseConsignmentProduct = (consignmentId: string, productId: string) => {
        navigate(`${location.pathname}/${consignmentId}/${productId}`);
    };

    const navigateToHouseConsignment = (consignmentId: string) => {
        navigate(`${location.pathname}/${consignmentId}`);
    };

    const editOrViewIcon = viewOnly ? <EyeOutlined /> : <EditOutlined />;
    const editOrViewButtonTooltip = viewOnly ? 'View' : 'Edit';

    /**
     * F11 identifies a house consignment by who ships it, F50 by the trade behind it, so the summary
     * names whichever pair of parties the message it belongs to actually carries.
     */
    const parties = data.goodsShipment
        ? [
              { label: 'Seller', name: data.goodsShipment.seller?.name },
              { label: 'Buyer', name: data.goodsShipment.buyer?.name },
          ]
        : [
              { label: 'Consignor', name: data.consignor?.name },
              { label: 'Consignee', name: data.consignee?.name },
          ];

    return (
        <CardItem error={isInvalid}>
            <HeaderRow>
                <HeaderLeft>
                    <IconWrap>
                        <HomeOutlined />
                    </IconWrap>

                    <ContentWrap>
                        <Space size={8} style={{ marginBottom: 2 }}>
                            <H2>House Consignment {consignmentNumber}</H2>
                            <DotSeparator />
                            <MetaText>{items.length} Items</MetaText>
                            {isInvalid && (
                                <InvalidIcon
                                    style={{ color: colors.error }}
                                    title={
                                        hasErrors
                                            ? 'This house consignment is invalid'
                                            : 'A goods item of this house consignment is invalid'
                                    }
                                />
                            )}
                        </Space>
                        <InfoRow>
                            {parties.map(({ label, name }) => (
                                <Space key={label} size={4}>
                                    <Typography.Text>{label}: </Typography.Text>
                                    <Strong>{name || EMPTY_VALUE}</Strong>
                                </Space>
                            ))}

                            <Space size={4}>
                                <Typography.Text>Ref. Number: </Typography.Text>
                                <Strong>{data.referenceNumberUCR?.referenceNumberUCR || EMPTY_VALUE}</Strong>
                            </Space>

                            <Space size={4}>
                                <Typography.Text>Place of Delivery: </Typography.Text>
                                <Strong>{data.placeOfDelivery?.location || EMPTY_VALUE}</Strong>
                            </Space>
                        </InfoRow>
                    </ContentWrap>
                </HeaderLeft>

                <Space size={8}>
                    <IconButton
                        onClick={() => data.id && navigateToHouseConsignment(data.id)}
                        icon={editOrViewIcon}
                        tooltipText={editOrViewButtonTooltip}
                    />
                    {!viewOnly && (
                        <IconButton
                            onClick={() => data.id && onDelete?.(data.id)}
                            icon={<DeleteOutlined />}
                            tooltipText="Delete"
                        />
                    )}
                </Space>
            </HeaderRow>

            <StyledCollapse ghost activeKey={activeKey} bordered={false} expandIcon={() => null} style={{ padding: 0 }}>
                <Panel key="details" header={null} showArrow={false}>
                    <Divider style={{ marginBlock: 16 }} />
                    <ProductsList>
                        {items.map((product, index) => {
                            const description = product.commodity?.descriptionOfGoods;
                            const hasItemErrors = invalidItemNumbers?.has(index + 1);

                            return (
                                <ProductRow key={product.id ?? index} error={hasItemErrors}>
                                    <ProductCell style={{ whiteSpace: 'nowrap' }}>
                                        <ShipmentIcon style={{ color: 'var(--polar-green-6)', marginRight: 4 }} />
                                        <Typography.Text>
                                            <Strong>Item {index + 1}</Strong>
                                        </Typography.Text>
                                        {hasItemErrors && (
                                            <InvalidIcon
                                                style={{ color: colors.error, marginLeft: 4 }}
                                                title="This item is invalid"
                                            />
                                        )}
                                    </ProductCell>

                                    <ProductCell>
                                        <Typography.Text style={{ whiteSpace: 'nowrap' }}>TARIC Code: </Typography.Text>
                                        <Strong>
                                            {product.commodity?.commodityCode?.harmonizedSystemSubHeadingCode ||
                                                EMPTY_VALUE}
                                        </Strong>
                                    </ProductCell>

                                    <ProductCell>
                                        <Typography.Text style={{ whiteSpace: 'nowrap' }}>
                                            Description:{' '}
                                        </Typography.Text>
                                        <Strong title={description}>{description || EMPTY_VALUE}</Strong>
                                    </ProductCell>

                                    <ProductCell>
                                        <Typography.Text style={{ whiteSpace: 'nowrap' }}>Gross Mass: </Typography.Text>
                                        <Strong>
                                            {formatMass(product.weight?.grossMass, GROSS_MASS_FORMAT) ?? EMPTY_VALUE}
                                        </Strong>
                                    </ProductCell>

                                    <ActionsCell>
                                        <Space size={8}>
                                            <IconButton
                                                onClick={() =>
                                                    data.id &&
                                                    product.id &&
                                                    navigateToHouseConsignmentProduct(data.id, product.id)
                                                }
                                                icon={editOrViewIcon}
                                                tooltipText={editOrViewButtonTooltip}
                                            />
                                            {!viewOnly && (
                                                <IconButton
                                                    onClick={() => product.id && onDeleteItem?.(product.id)}
                                                    icon={<DeleteOutlined />}
                                                    tooltipText="Delete"
                                                />
                                            )}
                                        </Space>
                                    </ActionsCell>
                                </ProductRow>
                            );
                        })}
                    </ProductsList>
                </Panel>
            </StyledCollapse>

            <FooterRow>
                {viewOnly ? (
                    // Use an empty div to keep the space between styling
                    <div></div>
                ) : (
                    <Button
                        size="small"
                        style={{ color: 'var(--brand-6)', borderColor: 'inherit' }}
                        icon={<PlusOutlined style={{ color: 'var(--brand-6)' }} />}
                        onClick={() => data.id && onAddItem?.(data.id)}
                    >
                        Add Item
                    </Button>
                )}

                <Button
                    type="link"
                    disabled={!items.length}
                    onClick={() => setSeeDetails((prev) => !prev)}
                    icon={seeDetails ? <UpOutlined /> : <DownOutlined />}
                >
                    {seeDetails ? 'Hide Details' : 'See Details'}
                </Button>
            </FooterRow>
        </CardItem>
    );
};

export default HouseConsignmentListItem;
